import { useMemo, useState } from 'react'

type StepId = 'challenge' | 'normalize' | 'negotiate' | 'commit' | 'execute' | 'receipt'

type Step = {
  id: StepId
  title: string
  tagline: string
  detail: string
  highlight: string[]
}

const steps: Step[] = [
  {
    id: 'challenge',
    title: '402 Challenge',
    tagline: 'A service returns Payment Required',
    detail:
      'A provider responds with HTTP 402 (headers + body). We capture the challenge and turn it into a negotiable, executable input.',
    highlight: ['Third‑party compatible', 'No private fields', 'Preserves raw evidence'],
  },
  {
    id: 'normalize',
    title: 'Normalize',
    tagline: 'Normalize into PaymentRequirement',
    detail:
      'Adapters turn different provider formats into one clear request: which chain, which asset, how much, and where it should go.',
    highlight: ['Clear asset + amount', 'Consistent format', 'Easy to extend'],
  },
  {
    id: 'negotiate',
    title: 'Negotiation',
    tagline: '8 rounds under guardrails',
    detail:
      'Negotiate within your limits on fees, time, and risk to reach better terms with clear reasoning.',
    highlight: ['User-defined limits', 'Clear pricing', 'Live updates'],
  },
  {
    id: 'commit',
    title: 'Commit Offer',
    tagline: 'Finalize terms and authorization',
    detail:
      'Turn the deal into an execution plan with clear caps and a deadline. The user signs once for the important limits.',
    highlight: ['One clear approval', 'Safe caps', 'Retry-safe'],
  },
  {
    id: 'execute',
    title: 'Execute',
    tagline: 'Swap / Bridge / Pay',
    detail:
      'Execute step-by-step: Solana swap, cross-chain intent, destination payment. Monitors track events and balance changes.',
    highlight: ['Observable execution', 'Circuit breakers', 'Reversible paths'],
  },
  {
    id: 'receipt',
    title: 'Receipt',
    tagline: 'Auditable outcome',
    detail:
      'Generate a receipt that shows what happened: transactions, fees, timing, and the final delivery—so the promise can be verified.',
    highlight: ['Easy to review', 'Transparent fees', 'Exact delivery'],
  },
]

export default function HowItWorks() {
  const [active, setActive] = useState<StepId>('challenge')
  const activeStep = useMemo(() => steps.find((s) => s.id === active) ?? steps[0], [active])

  return (
    <section id="how" className="bg-bg">
      <div className="container-x py-14 md:py-18">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight md:text-3xl">How it works</h2>
            <p className="mt-2 max-w-3xl text-white/65">
              A path from “payment challenge” to “auditable receipt”. Each step has clear inputs, outputs, constraints, and evidence.
            </p>
          </div>
            <div className="flex flex-wrap gap-2">
              <a
                className="inline-flex items-center justify-center rounded-full border border-white/10 bg-white/5 px-5 py-2 text-sm font-semibold text-white/85 transition hover:bg-white/10 hover:text-white"
                href="#agents"
              >
                Meet the agents
              </a>
              <a
                className="inline-flex items-center justify-center rounded-full border border-white/10 bg-white/5 px-5 py-2 text-sm font-semibold text-white/85 transition hover:bg-white/10 hover:text-white"
                href="#roadmap"
              >
                View roadmap
              </a>
            </div>
        </div>

        <div className="mt-8 grid gap-5 md:grid-cols-12">
          <div className="md:col-span-5">
            <div className="glass p-4">
              <div className="grid gap-2">
                {steps.map((s, idx) => (
                  <StepButton
                    key={s.id}
                    index={idx + 1}
                    title={s.title}
                    tagline={s.tagline}
                    active={s.id === active}
                    onClick={() => setActive(s.id)}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="glass2 p-7">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-semibold text-white/90">{activeStep.title}</div>
                  <div className="mt-1 text-xs text-white/55">{activeStep.tagline}</div>
                </div>
                <span className="rounded-full bg-glass-gradient px-3 py-1 text-xs font-semibold text-white/80">
                  Step {steps.findIndex((s) => s.id === activeStep.id) + 1}
                </span>
              </div>
              <p className="mt-5 text-sm leading-relaxed text-white/70">{activeStep.detail}</p>
              <div className="mt-6 flex flex-wrap gap-2">
                {activeStep.highlight.map((h) => (
                  <span
                    key={h}
                    className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/70"
                  >
                    {h}
                  </span>
                ))}
              </div>
              <div className="mt-7 rounded-xl border border-white/10 bg-black/20 p-4">
                <div className="text-xs text-white/55">Delivery standards (our public promises)</div>
                <div className="mt-2 grid gap-2 md:grid-cols-2">
                  <PromiseItem title="Exact Amount" desc="Deliver the exact target asset amount" />
                  <PromiseItem title="Max Fee Guardrail" desc="Never exceed the user-defined fee cap" />
                  <PromiseItem title="Deadline" desc="Timeouts with explainable fallback / refunds" />
                  <PromiseItem title="Auditable Receipt" desc="Evidence and receipts for every step" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function StepButton(props: {
  index: number
  title: string
  tagline: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      className={[
        'w-full rounded-xl border px-4 py-3 text-left transition',
        props.active ? 'border-primary/40 bg-white/5' : 'border-white/10 bg-transparent hover:bg-white/5',
      ].join(' ')}
    >
      <div className="flex items-center gap-3">
        <span
          className={[
            'flex h-7 w-7 items-center justify-center rounded-lg text-xs font-semibold',
            props.active ? 'bg-primary text-black' : 'bg-white/5 text-white/70',
          ].join(' ')}
        >
          {props.index}
        </span>
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-white/90">{props.title}</div>
          <div className="truncate text-xs text-white/50">{props.tagline}</div>
        </div>
      </div>
    </button>
  )
}

function PromiseItem(props: { title: string; desc: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-3">
      <div className="text-xs font-semibold text-white/85">{props.title}</div>
      <div className="mt-1 text-xs text-white/55">{props.desc}</div>
    </div>
  )
}
